import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { FREE_DAILY_SEARCHES } from '../config/quotas';
import { Input } from '../components/ui/input';
import {
  Users,
  Search,
  UserCheck,
  UserX,
  RefreshCw,
  Loader2,
  Calendar,
  Shield,
  Crown,
  User,
  Stethoscope,
  HeartPulse,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminUsersPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [filterProfile, setFilterProfile] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [activeTab, setActiveTab] = useState('patients');

  const updateProStatus = async (userId, newStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), { status: newStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
    } catch (e) {
      console.error('Erreur mise à jour statut:', e);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, search, filterProfile, filterVerified, activeTab]);

  const loadUsers = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const allUsers = snap.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            // 🆕 emailVerified depuis Firestore si stocké, sinon Google = toujours vérifié
            emailVerified: data.emailVerified ?? (data.profile_picture ? true : false), // Google users ont une photo
          };
        })
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setUsers(allUsers);
    } catch (e) {
      console.error('Erreur chargement users:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFilters = () => {
    let result = [...users];

    if (activeTab === 'patients') {
      result = result.filter((u) => u.profile_type === 'non_medical');
    } else {
      result = result.filter((u) => ['infirmier', 'medecin'].includes(u.profile_type));
    }

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.first_name?.toLowerCase().includes(s) ||
          u.last_name?.toLowerCase().includes(s) ||
          u.email?.toLowerCase().includes(s)
      );
    }

    if (filterProfile !== 'all') {
      result = result.filter((u) => u.profile_type === filterProfile);
    }

    if (filterVerified === 'verified') {
      result = result.filter((u) => u.emailVerified === true);
    } else if (filterVerified === 'unverified') {
      result = result.filter((u) => u.emailVerified !== true);
    }

    setFiltered(result);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getProfileLabel = (type) => {
    switch (type) {
      case 'non_medical':
        return {
          label: 'Patient / Public',
          color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
          icon: <User className="w-3 h-3" />,
        };
      case 'infirmier':
        return {
          label: 'Infirmier',
          color: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
          icon: <HeartPulse className="w-3 h-3" />,
        };
      case 'medecin':
        return {
          label: 'Médecin',
          color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
          icon: <Stethoscope className="w-3 h-3" />,
        };
      default:
        return {
          label: 'Inconnu',
          color: 'bg-gray-100 text-gray-500',
          icon: <User className="w-3 h-3" />,
        };
    }
  };

  const getSubscriptionLabel = (type) => {
    if (type === 'premium')
      return (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">
          <Crown className="w-3 h-3" /> Premium
        </span>
      );
    return (
      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full text-xs">
        Gratuit
      </span>
    );
  };

  // Stats rapides
  const totalVerified = users.filter((u) => u.emailVerified === true).length;
  const totalPremium = users.filter((u) => u.subscription_type === 'premium').length;
  const totalPending = users.filter((u) => ['infirmier', 'medecin'].includes(u.profile_type) && u.status === 'pending').length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              Utilisateurs
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {users.length} compte{users.length > 1 ? 's' : ''} enregistré
              {users.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button
            onClick={() => loadUsers(true)}
            variant="outline"
            size="sm"
            disabled={refreshing}
            className="dark:border-gray-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: users.length, icon: Users, color: 'text-blue-500' },
            { label: 'Email vérifié', value: totalVerified, icon: UserCheck, color: 'text-green-500' },
            { label: 'Premium', value: totalPremium, icon: Crown, color: 'text-amber-500' },
            { label: 'Pro en attente', value: totalPending, icon: Clock, color: 'text-orange-500' },
          ].map((s, i) => (
            <Card
              key={i}
              className="p-4 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800"
            >
              <div className="flex items-center gap-3">
                <s.icon className={`w-8 h-8 ${s.color}`} />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{s.value}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Onglets */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('patients')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'patients' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`}
          >
            Patients
          </button>
          <button
            onClick={() => setActiveTab('pro')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'pro' ? 'bg-green-500 text-white' : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`}
          >
            Professionnels
            {totalPending > 0 && (
              <span className="px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded-full">{totalPending}</span>
            )}
          </button>
        </div>

        {/* Filtres */}
        <Card className="p-4 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Recherche */}
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 dark:bg-gray-800 dark:border-gray-700"
              />
            </div>

            {/* Filtre profil */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterProfile}
                onChange={(e) => setFilterProfile(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <option value="all">Tous les profils</option>
                <option value="non_medical">Patient / Public</option>
                <option value="infirmier">Infirmier</option>
                <option value="medecin">Médecin</option>
              </select>
            </div>

            {/* Filtre email vérifié */}
            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="all">Email: tous</option>
              <option value="verified">Email vérifié</option>
              <option value="unverified">Email non vérifié</option>
            </select>

            {/* Résultats */}
            <span className="text-sm text-gray-400 ml-auto">
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </span>
          </div>
        </Card>

        {/* Tableau */}
        <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Profil</th>
                  {activeTab === 'pro' && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">RPPS</th>
                  )}
                  {activeTab === 'pro' && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Statut</th>
                  )}
                  {activeTab === 'patients' && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Abonnement</th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Email vérifié</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Inscription</th>
                  {activeTab === 'patients' && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Recherches/jour</th>
                  )}
                  {activeTab === 'pro' && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-400">
                      Aucun utilisateur trouvé
                    </td>
                  </tr>
                ) : (
                  filtered.map((user) => {
                    const profile = getProfileLabel(user.profile_type);
                    return (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        {/* Nom + Email */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                              {user.first_name?.[0]}
                              {user.last_name?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {user.first_name} {user.last_name}
                                {user.role === 'admin' && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded font-medium">
                                    Admin
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-400">{user.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* Profil */}
                        <td className="px-4 py-3">
                          <span
                            className={`flex items-center gap-1.5 w-fit px-2 py-1 rounded-full text-xs font-medium ${profile.color}`}
                          >
                            {profile.icon}
                            {profile.label}
                          </span>
                        </td>

                        {activeTab === 'pro' && (
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{user.rpps || '—'}</span>
                          </td>
                        )}
                        {activeTab === 'pro' && (
                          <td className="px-4 py-3">
                            {user.status === 'active' ? (
                              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle className="w-3.5 h-3.5" /> Actif</span>
                            ) : user.status === 'rejected' ? (
                              <span className="flex items-center gap-1 text-xs text-red-500"><XCircle className="w-3.5 h-3.5" /> Rejeté</span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-orange-500"><Clock className="w-3.5 h-3.5" /> En attente</span>
                            )}
                          </td>
                        )}
                        {activeTab === 'patients' && (
                          <td className="px-4 py-3">{getSubscriptionLabel(user.subscription_type)}</td>
                        )}

                        {/* Email vérifié */}
                        <td className="px-4 py-3">
                          {user.emailVerified ? (
                            <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                              <UserCheck className="w-4 h-4" />
                              <span className="text-xs">Vérifié</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-orange-500 dark:text-orange-400">
                              <UserX className="w-4 h-4" />
                              <span className="text-xs">Non vérifié</span>
                            </div>
                          )}
                        </td>

                        {/* Date inscription */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                            <Calendar className="w-3.5 h-3.5" />
                            <span className="text-xs">{formatDate(user.createdAt)}</span>
                          </div>
                        </td>

                        {activeTab === 'patients' && (
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {user.daily_search_count ?? 0}
                              <span className="text-xs text-gray-400 ml-1">/ {FREE_DAILY_SEARCHES}</span>
                            </span>
                          </td>
                        )}
                        {activeTab === 'pro' && (
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {user.status !== 'active' && (
                                <button
                                  onClick={() => updateProStatus(user.id, 'active')}
                                  className="flex items-center gap-1 px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg transition-colors"
                                >
                                  <CheckCircle className="w-3 h-3" /> Activer
                                </button>
                              )}
                              {user.status !== 'rejected' && (
                                <button
                                  onClick={() => updateProStatus(user.id, 'rejected')}
                                  className="flex items-center gap-1 px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg transition-colors"
                                >
                                  <XCircle className="w-3 h-3" /> Rejeter
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
